/**
 * A FICHA DE IDA — o papel que sai com a equipe.
 *
 * Os quatro PDFs do `workflowPDF` imprimem o que JÁ FOI LANÇADO: são documento
 * de saída, e só existem depois do trabalho digitado. Este arquivo faz o
 * contrário — imprime o que ainda vai ser escrito à mão, na fazenda ou na
 * bancada.
 *
 * POR QUE O NÚMERO DO SERVIÇO VEM GRANDE
 *
 * O papel demora a voltar. Medido nas sessões em produção: a OPU é lançada em
 * média 10 dias depois do campo, a FIV 12 e a TE 6,5 — com casos de 22 dias. E
 * o laboratório roda vários serviços ao mesmo tempo. Com essa distância, o que
 * se perde não é o dado, que está escrito: é DE QUEM É AQUELE PAPEL. Um "Cód:
 * 2026-0054" em cinza de 11px, como sai nos PDFs de hoje, não se acha numa
 * pilha em cima da mesa; por isso aqui ele é o maior elemento da folha, e se
 * repete no rodapé de toda página.
 *
 * A REGRA, UMA SÓ
 *
 *   A ficha de uma etapa vem preenchida com o que a etapa ANTERIOR entregou.
 *
 *   OPU   não tem etapa antes, então sai limpa — no dia de ir a campo não
 *         existe doadora cadastrada, elas nascem na digitação da volta.
 *   FIV   puxa os pares doadora × touro que a OPU produziu.
 *   TE    puxa os embriões que a FIV produziu.
 *   DG30  puxa as receptoras que a TE transferiu.
 *   DG60  puxa as do DG30 que não deram vazia — prenhe e inconclusivo voltam
 *         ao ultrassom; vazia não tem o que olhar.
 *
 * Sem o anterior lançado, a busca volta vazia e a ficha sai limpa sozinha —
 * não há caso especial para escrever, e é isso que faz a regra valer para as
 * três. O efeito colateral é bom: quem lança a etapa na hora ganha a próxima
 * ficha datilografada; quem não lança escreve os quarenta e três à mão.
 *
 * No DG o resultado é de MARCAR, não de escrever: uma coluna por desfecho, com
 * um quadradinho. Em pé no curral, um X sai mais rápido e mais legível que
 * "inconclusivo" escrito à mão.
 *
 * O QUE É IMPRESSO E O QUE É ESCRITO
 *
 * Célula que já vem preenchida tem fundo cinza; célula para escrever é branca.
 * É a única cor da folha, e ela responde à única pergunta que quem segura a
 * prancheta faz: onde eu escrevo. Não é estado, é affordance.
 */
import { fetchOpuSession, fetchOpuDonors } from '../data/opu';
import { fetchFreshEmbryosForTe } from '../data/embryos';
import { fetchTeSessionRef, fetchApprovedTransfers } from '../data/te';
import { fetchDgSessions, fetchDgChecks } from '../data/dg';
import { linhaParaCheck } from './dgMapeamento';
import { DG30_RESULTS } from '../constants/workflowGranular';
import { etapaPendente } from '../constants/schedule';
import { baseCSS, openPrint, fmt, n, STAGE_LABELS } from './workflowPDF';
import {
  identidadeDoLaboratorio, assinaturaDoRodape, linhaDoResponsavel,
} from './laboratorioNoRelatorio';

/** As etapas que ganham ficha. As demais não vão a lugar nenhum com papel. */
export const ETAPAS_COM_FICHA = new Set(['opu', 'fiv', 'te', 'dg30', 'dg60']);

export const temFichaDeCampo = (etapaId) => ETAPAS_COM_FICHA.has(etapaId);

/**
 * Qual ficha este serviço imprime, ou nada.
 *
 * `preferida` é o contexto de quem chamou — a coluna do Kanban, o tipo do
 * evento no calendário. Quem chama de um lugar sem contexto (a linha da
 * tabela) não passa nada, e aí vale a etapa pendente, que é a mesma regra que
 * o cartão já usa para decidir qual aba abrir.
 */
export const etapaDaFicha = (service, preferida = null) => {
  if (temFichaDeCampo(preferida)) return preferida;
  const pendente = etapaPendente(service)?.id || null;
  return temFichaDeCampo(pendente) ? pendente : null;
};

/**
 * Vinte linhas na ficha em branco, que é o que o bloco de papel de hoje tem.
 *
 * Não é número redondo por acaso: a maior OPU em produção teve 24 doadoras e a
 * mediana fica perto de 18. Vinte cobre o dia comum e a segunda folha resolve o
 * resto — melhor que quarenta linhas vazias em toda aspiração de doze.
 */
const LINHAS_EM_BRANCO = 20;

/**
 * Três linhas de sobra nas fichas que vêm preenchidas.
 *
 * A realidade traz mais uma: numa OPU de setembro apareceram 23 doadoras onde
 * o sistema conhecia 16. Linha de reserva custa três centímetros de papel;
 * a falta dela custa uma anotação na margem que ninguém digita depois.
 */
const LINHAS_DE_RESERVA = 3;

const fichaCSS = () => `${baseCSS()}
  /* O número do serviço é o maior elemento da folha. É o motivo dela existir:
     o papel volta dez dias depois, e precisa dizer de quem é sem ninguém
     precisar ler. */
  .codigao { font-family: "Courier New", monospace; font-size: 26px; font-weight: 800;
             color: #334155; line-height: 1; letter-spacing: .02em; }

  /* Altura de linha para letra de gente escrevendo em pé, com prancheta. */
  .ficha td { height: 26px; padding: 2px 6px; border-bottom: 1px solid #cbd5e1;
              border-right: 1px solid #e2e8f0; }
  .ficha td:last-child { border-right: none; }
  .ficha th { border-right: 1px solid #cbd5e1; }
  .ficha th:last-child { border-right: none; }

  /* Cinza = já veio impresso. Branco = escreva aqui. Única cor da folha. */
  .vem-pronto { background: #f8fafc; color: #1a1a1a; }
  .escreva { background: #fff; }
  .divisa { border-left: 2px solid #94a3b8; }

  /* Campo de cabeçalho para preencher à mão. */
  .preencher { display: inline-block; border-bottom: 1px solid #94a3b8;
               min-width: 130px; height: 15px; }
  .preencher.curto { min-width: 66px; }

  /* Onde se marca um X, centrado na célula estreita de cada desfecho. */
  .marcar { text-align: center; }
  .marcar::before { content: ""; display: inline-block; width: 12px; height: 12px;
                    border: 1.5px solid #64748b; border-radius: 2px; vertical-align: middle; }

  .assinaturas { margin-top: 22px; display: flex; gap: 36px; }
  .assinaturas div { flex: 1; border-top: 1px solid #94a3b8; padding-top: 4px;
                     font-size: 10px; color: #64748b; text-align: center; }
  .rodape-ficha { margin-top: 12px; padding-top: 6px; border-top: 1px solid #e2e8f0;
                  display: flex; justify-content: space-between;
                  font-size: 10px; color: #64748b; }`;

const cabecalho = (service, titulo) => `
  <div class="page-header">
    ${identidadeDoLaboratorio()}
    <div class="header-right">
      <div class="codigao">${n(service.service_code)}</div>
      <div class="client">${n(service.client_name)}</div>
      ${service.service_label ? `<div class="meta">${service.service_label}</div>` : ''}
    </div>
  </div>
  <div class="section-title">${titulo}</div>`;

const rodape = (service, etapa) => `
  <div class="assinaturas">
    <div>Responsável pelo lançamento</div>
    <div>Responsável pela conferência</div>
  </div>
  <div class="rodape-ficha">
    <span>${assinaturaDoRodape('FIVPRO')} · Ficha de campo · ${etapa}</span>
    <span><b>Serviço ${n(service.service_code)}</b> · ${n(service.client_name)}</span>
  </div>
  ${linhaDoResponsavel()}`;

/*
 * As fichas são EXPORTADAS por causa do teste, e o teste existe por uma
 * razão só: o número de células de cada linha precisa bater com o de colunas
 * do cabeçalho. Errar isso não quebra nada — a tabela só sai torta no papel, e
 * o erro aparece na fazenda, com a prancheta na mão. Foi o que quase aconteceu
 * ao encaixar a coluna de RGD na ficha da TE, que tem três formatos de linha
 * (preenchida, reserva e vazia) e cada um conta as suas células.
 */

/** `quantidade` linhas de `colunas` células vazias, numeradas a partir de `de`. */
const linhasVazias = (colunas, quantidade, de = 1) =>
  Array.from({ length: quantidade }, (_, i) =>
    `<tr><td class="num">${de + i}</td>${'<td class="escreva"></td>'.repeat(colunas)}</tr>`
  ).join('');

// ─── OPU: a única que sai inteira em branco ──────────────────────────────────

export const fichaOPU = (service) => `
  <style>${fichaCSS()}</style>
  ${cabecalho(service, 'OPU — Ficha de campo')}
  <div class="session-info">
    <span><b>Data agendada:</b> ${fmt(service.opu_scheduled_date)}</span>
    <span><b>Data realizada:</b> <span class="preencher curto"></span></span>
    <span><b>Hora início:</b> <span class="preencher curto"></span></span>
    <span><b>Fazenda:</b> <span class="preencher"></span></span>
    <span><b>Técnico:</b> <span class="preencher"></span></span>
  </div>
  <table class="ficha">
    <thead><tr>
      <th>#</th><th style="width:20%">Doadora</th><th style="width:12%">RGD</th>
      <th style="width:10%">Raça</th><th style="width:16%">Touro</th>
      <th style="width:8%">Hora</th>
      <th style="text-align:center;width:7%">Desn.</th>
      <th style="text-align:center;width:7%">Viáveis</th>
      <th style="text-align:center;width:7%">Total</th>
      <th>Obs</th>
    </tr></thead>
    <tbody>${linhasVazias(9, LINHAS_EM_BRANCO)}</tbody>
  </table>
  ${rodape(service, 'OPU')}`;

// ─── FIV: os pares vêm da OPU ────────────────────────────────────────────────

export const fichaFIV = (service, doadoras = []) => {
  const preenchidas = doadoras.map((d, i) => `
    <tr>
      <td class="num">${i + 1}</td>
      <td class="vem-pronto">${n(d.animal_identifier)}</td>
      <td class="vem-pronto">${n(d.rgd)}</td>
      <td class="vem-pronto">${n(d.breed)}</td>
      <td class="vem-pronto">${n(d.bull_name)}</td>
      <td class="vem-pronto" style="text-align:center">${d.oocytes_viable ?? ''}</td>
      <td class="escreva divisa"></td>
      ${'<td class="escreva"></td>'.repeat(5)}
      <td class="escreva"></td>
    </tr>`).join('');

  const reserva = Array.from({ length: LINHAS_DE_RESERVA }, (_, i) => `
    <tr>
      <td class="num">${doadoras.length + i + 1}</td>
      ${'<td class="escreva"></td>'.repeat(5)}
      <td class="escreva divisa"></td>
      ${'<td class="escreva"></td>'.repeat(6)}
    </tr>`).join('');

  const vazia = linhasVazias(12, LINHAS_EM_BRANCO);

  return `
  <style>${fichaCSS()}</style>
  ${cabecalho(service, 'FIV — Ficha de bancada')}
  <div class="session-info">
    <span><b>Data da OPU:</b> ${fmt(service.opu_date || service.opu_scheduled_date)}</span>
    <span><b>Data recepção:</b> <span class="preencher curto"></span></span>
    <span><b>Embriologista:</b> <span class="preencher"></span></span>
    ${doadoras.length === 0
      ? '<span style="color:#64748b">OPU ainda não lançada — ficha em branco</span>'
      : `<span><b>Doadoras da OPU:</b> ${doadoras.length}</span>`}
  </div>
  <table class="ficha">
    <thead><tr>
      <th>#</th><th style="width:17%">Doadora</th><th style="width:10%">RGD</th>
      <th style="width:9%">Raça</th><th style="width:14%">Touro</th>
      <th style="text-align:center;width:6%">Viáv.</th>
      <th class="divisa" style="text-align:center;width:7%">Cliv.</th>
      <th style="text-align:center;width:5%">BI</th>
      <th style="text-align:center;width:5%">BL</th>
      <th style="text-align:center;width:5%">BX</th>
      <th style="text-align:center;width:5%">BN</th>
      <th style="text-align:center;width:5%">BE</th>
      <th>Obs</th>
    </tr></thead>
    <tbody>${doadoras.length > 0 ? preenchidas + reserva : vazia}</tbody>
  </table>
  ${rodape(service, 'FIV')}`;
};

// ─── TE: os embriões vêm da FIV ──────────────────────────────────────────────

export const fichaTE = (service, embrioes = []) => {
  const preenchidas = embrioes.map((e) => `
    <tr>
      <td class="num">${n(e.service_number)}</td>
      <td class="vem-pronto">${n(e.donor?.identifier)}</td>
      <td class="vem-pronto">${n(e.donor?.rgd)}</td>
      <td class="vem-pronto">${n(e.bull_name)}</td>
      <td class="vem-pronto">${n(e.donor?.breed)}</td>
      <td class="vem-pronto" style="text-align:center">${STAGE_LABELS[e.stage] || n(e.stage)}</td>
      <td class="escreva divisa"></td>
      ${'<td class="escreva"></td>'.repeat(3)}
    </tr>`).join('');

  const reserva = Array.from({ length: LINHAS_DE_RESERVA }, () => `
    <tr>
      <td class="num"></td>
      ${'<td class="escreva"></td>'.repeat(5)}
      <td class="escreva divisa"></td>
      ${'<td class="escreva"></td>'.repeat(3)}
    </tr>`).join('');

  const vazia = linhasVazias(9, LINHAS_EM_BRANCO);

  return `
  <style>${fichaCSS()}</style>
  ${cabecalho(service, 'TE — Ficha de campo')}
  <div class="session-info">
    <span><b>Data agendada:</b> ${fmt(service.te_scheduled_date)}</span>
    <span><b>Data realizada:</b> <span class="preencher curto"></span></span>
    <span><b>Hora entrega:</b> <span class="preencher curto"></span></span>
    <span><b>Local:</b> <span class="preencher"></span></span>
    <span><b>Técnico:</b> <span class="preencher"></span></span>
    ${embrioes.length === 0
      ? '<span style="color:#64748b">FIV ainda não lançada — ficha em branco</span>'
      : `<span><b>Embriões:</b> ${embrioes.length}</span>`}
  </div>
  <table class="ficha">
    <thead><tr>
      <th style="width:6%">Emb.</th><th style="width:16%">Doadora</th>
      <th style="width:10%">RGD</th>
      <th style="width:14%">Touro</th><th style="width:8%">Raça</th>
      <th style="text-align:center;width:7%">Fase</th>
      <th class="divisa" style="width:17%">Receptora</th>
      <th style="text-align:center;width:6%">CL</th>
      <th style="text-align:center;width:6%">Grau</th>
      <th>Obs</th>
    </tr></thead>
    <tbody>${embrioes.length > 0 ? preenchidas + reserva : vazia}</tbody>
  </table>
  ${rodape(service, 'TE')}`;
};

// ─── DG: o resultado se marca, não se escreve ────────────────────────────────

/** Data do banco, venha ela como `2026-09-20` ou como timestamp inteiro. */
const dia = (d) => (d ? fmt(String(d).slice(0, 10)) : '');

const embriao = (num) => (num != null ? `#${num}` : '');

const ROTULO_DG30 = {
  [DG30_RESULTS.PREGNANT]: 'Prenhe',
  [DG30_RESULTS.INCONCLUSIVE]: 'Inconcl.',
};

/** As do DG30 que voltam ao ultrassom no DG60: tudo que não deu vazia. */
export const receptorasParaDG60 = (checksDG30 = []) =>
  checksDG30.filter(c => c.result !== DG30_RESULTS.EMPTY);

/**
 * As linhas de uma ficha de DG. As duas têm o mesmo corpo — três colunas que
 * vêm prontas e um lado de marcar —, então os três formatos de linha
 * (preenchida, reserva e vazia) moram num lugar só e contam as células juntos.
 */
const linhasDoDG = (itens, prontas, ladoDeMarcar) => {
  const preenchidas = itens.map((item, i) => `
    <tr>
      <td class="num">${i + 1}</td>
      ${prontas(item)}
      ${ladoDeMarcar}
    </tr>`).join('');
  const emBranco = (quantas, de) => Array.from({ length: quantas }, (_, i) => `
    <tr>
      <td class="num">${de + i}</td>
      ${'<td class="escreva"></td>'.repeat(3)}
      ${ladoDeMarcar}
    </tr>`).join('');
  return itens.length > 0
    ? preenchidas + emBranco(LINHAS_DE_RESERVA, itens.length + 1)
    : emBranco(LINHAS_EM_BRANCO, 1);
};

// Prenhe · Vazia · Inconcl. para marcar; Batim. · Tam. · Obs para escrever.
const MARCAR_DG30 = '<td class="escreva marcar divisa"></td>'
  + '<td class="escreva marcar"></td>'.repeat(2)
  + '<td class="escreva"></td>'.repeat(3);

export const fichaDG30 = (service, transferencias = []) => `
  <style>${fichaCSS()}</style>
  ${cabecalho(service, 'DG 30 dias — Ficha de campo')}
  <div class="session-info">
    <span><b>Data agendada:</b> ${fmt(service.pregnancy_30d_scheduled_date)}</span>
    <span><b>Data realizada:</b> <span class="preencher curto"></span></span>
    <span><b>Método:</b> <span class="preencher curto"></span></span>
    <span><b>Local:</b> <span class="preencher"></span></span>
    <span><b>Técnico:</b> <span class="preencher"></span></span>
    ${transferencias.length === 0
      ? '<span style="color:#64748b">TE ainda não lançada — ficha em branco</span>'
      : `<span><b>Receptoras:</b> ${transferencias.length}</span>`}
  </div>
  <table class="ficha">
    <thead><tr>
      <th>#</th><th style="width:22%">Receptora</th>
      <th style="text-align:center;width:7%">Emb.</th>
      <th style="width:10%">DG prev.</th>
      <th class="divisa" style="text-align:center;width:7%">Prenhe</th>
      <th style="text-align:center;width:7%">Vazia</th>
      <th style="text-align:center;width:7%">Inconcl.</th>
      <th style="text-align:center;width:7%">Batim.</th>
      <th style="text-align:center;width:8%">Tam. mm</th>
      <th>Obs / causa</th>
    </tr></thead>
    <tbody>${linhasDoDG(transferencias, (t) => `
      <td class="vem-pronto">${n(t.recipient_identifier)}</td>
      <td class="vem-pronto" style="text-align:center">${embriao(t.embryo_number)}</td>
      <td class="vem-pronto">${dia(t.expected_dg30)}</td>`, MARCAR_DG30)}</tbody>
  </table>
  ${rodape(service, 'DG30')}`;

// Prenhe · Abortou para marcar; Batim. · Sexo · Obs para escrever.
const MARCAR_DG60 = '<td class="escreva marcar divisa"></td>'
  + '<td class="escreva marcar"></td>'
  + '<td class="escreva"></td>'.repeat(3);

export const fichaDG60 = (service, checksDG30 = []) => `
  <style>${fichaCSS()}</style>
  ${cabecalho(service, 'DG 60 dias — Ficha de campo')}
  <div class="session-info">
    <span><b>Data agendada:</b> ${fmt(service.pregnancy_60d_scheduled_date)}</span>
    <span><b>Data realizada:</b> <span class="preencher curto"></span></span>
    <span><b>Método:</b> <span class="preencher curto"></span></span>
    <span><b>Local:</b> <span class="preencher"></span></span>
    <span><b>Técnico:</b> <span class="preencher"></span></span>
    ${checksDG30.length === 0
      ? '<span style="color:#64748b">DG30 ainda não lançado — ficha em branco</span>'
      : `<span><b>Receptoras:</b> ${checksDG30.length}</span>`}
  </div>
  <table class="ficha">
    <thead><tr>
      <th>#</th><th style="width:22%">Receptora</th>
      <th style="text-align:center;width:7%">Emb.</th>
      <th style="text-align:center;width:9%">DG30</th>
      <th class="divisa" style="text-align:center;width:7%">Prenhe</th>
      <th style="text-align:center;width:7%">Abortou</th>
      <th style="text-align:center;width:7%">Batim.</th>
      <th style="text-align:center;width:9%">Sexo</th>
      <th>Obs</th>
    </tr></thead>
    <tbody>${linhasDoDG(checksDG30, (c) => `
      <td class="vem-pronto">${n(c.recipient_identifier)}</td>
      <td class="vem-pronto" style="text-align:center">${embriao(c.embryo_number)}</td>
      <td class="vem-pronto" style="text-align:center">${ROTULO_DG30[c.result] || ''}</td>`, MARCAR_DG60)}</tbody>
  </table>
  ${rodape(service, 'DG60')}`;

// ─── A porta ─────────────────────────────────────────────────────────────────

/**
 * Imprime a ficha da etapa, buscando sozinha o que a etapa anterior deixou.
 *
 * A busca falhando NÃO impede a ficha: o papel em branco continua servindo, e
 * quem está de saída para a fazenda não pode ficar sem ele porque uma consulta
 * demorou. Por isso o `catch` devolve lista vazia em vez de subir o erro.
 */
export const gerarFichaDeCampo = async (service, etapaId, { laboratoryId } = {}) => {
  const etapa = etapaDaFicha(service, etapaId);
  if (!etapa) return false;

  if (etapa === 'opu') {
    openPrint(`Ficha OPU – ${service.service_code}`, fichaOPU(service));
    return true;
  }

  if (etapa === 'fiv') {
    let doadoras = [];
    try {
      const sessao = await fetchOpuSession(service.id);
      if (sessao?.id) doadoras = (await fetchOpuDonors(sessao.id)) || [];
    } catch { doadoras = []; }
    openPrint(`Ficha FIV – ${service.service_code}`, fichaFIV(service, doadoras));
    return true;
  }

  if (etapa === 'dg30') {
    let transferencias = [];
    try {
      const sessao = await fetchTeSessionRef(service.id);
      if (sessao?.id) transferencias = (await fetchApprovedTransfers(sessao.id)) || [];
    } catch { transferencias = []; }
    openPrint(`Ficha DG30 – ${service.service_code}`, fichaDG30(service, transferencias));
    return true;
  }

  if (etapa === 'dg60') {
    let checks = [];
    try {
      const sessoes = (await fetchDgSessions(service.id)) || [];
      const dg30 = sessoes.find(x => x.check_type === 'DG30');
      if (dg30?.id) checks = ((await fetchDgChecks(dg30.id)) || []).map(linhaParaCheck);
    } catch { checks = []; }
    openPrint(`Ficha DG60 – ${service.service_code}`, fichaDG60(service, receptorasParaDG60(checks)));
    return true;
  }

  let embrioes = [];
  try {
    embrioes = (await fetchFreshEmbryosForTe(service, laboratoryId)) || [];
  } catch { embrioes = []; }
  openPrint(`Ficha TE – ${service.service_code}`, fichaTE(service, embrioes));
  return true;
};
